// ---------------------------------------------------------------------------
// Shared activity-type registry - single source of truth for both the backend
// (client-project-activity domain, invoice rendering, MCP tool descriptions)
// and the frontend (wizard, activity cards, diary, invoice line items).
//
// This module intentionally carries NO Zod schemas, NO UI form definitions,
// and NO logger imports. It is the pure data + pure-function layer.
//
// Two registries:
//   - UNIT_TYPE_REGISTRY: one entry per billing unit (hours, km, pages, ...)
//     carrying the singular/plural display labels.
//   - ACTIVITY_TYPE_REGISTRY: one entry per activity type (MEETING, TRAVEL_CAR,
//     ...) carrying its display label, default unit type, the allowed unit
//     types a user may override to, and the registry's default isDisbursement
//     value. Each activity row stores its own `unitType` and `isDisbursement`
//     on the row, so the registry's defaults are pre-fills only, never the
//     authoritative source after create.
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Unit types - the set of billing units the system knows about.
// ---------------------------------------------------------------------------
export const UNIT_TYPES = [
    'hours',
    'km',
    'pages',
    'appearances',
    'items',
    'messages',
    'documents',
    'lump_sum',
];
export const UNIT_TYPE_REGISTRY = {
    hours: { label: 'Hours', labelSingular: 'hour', labelPlural: 'hours', labelShorthand: 'hr' },
    km: { label: 'Kilometres', labelSingular: 'km', labelPlural: 'km', labelShorthand: 'km' },
    pages: { label: 'Pages', labelSingular: 'page', labelPlural: 'pages', labelShorthand: 'page' },
    appearances: { label: 'Appearances', labelSingular: 'appearance', labelPlural: 'appearances', labelShorthand: 'appearance' },
    items: { label: 'Items', labelSingular: 'item', labelPlural: 'items', labelShorthand: 'item' },
    messages: { label: 'Messages', labelSingular: 'message', labelPlural: 'messages', labelShorthand: 'message' },
    documents: { label: 'Documents', labelSingular: 'document', labelPlural: 'documents', labelShorthand: 'document' },
    lump_sum: { label: 'Lump sum', labelSingular: 'lump sum', labelPlural: 'lump sum', labelShorthand: 'lump sum' },
};
/**
 * Narrow a free-form string into the UnitType tagged union. Use at trust
 * boundaries (query params, legacy DB reads) where a string arrives that
 * should be a UnitType but isn't statically typed as one.
 */
export function isUnitType(value) {
    return UNIT_TYPES.includes(value);
}
/**
 * Pick the right singular/plural form of a unit type for a given count.
 * English convention: singular only when count is exactly 1; plural otherwise
 * (including 0 and decimals like 0.5 or 2.5).
 *
 * Previously carried a `hours stays plural at count 1` exception for timesheet
 * convention ("1 hours"); removed in favour of standard English because the
 * invoice and activity surfaces now render hour-valued durations via
 * `formatDuration` ("1 hour 30 minutes"), which needs the singular form for
 * exact-1 counts. Callers that still reach `unitLabelForCount` with `unit='hours'`
 * will now get "1 hour" / "2 hours" - the more natural form.
 */
export function unitLabelForCount(unit, count) {
    const { labelSingular, labelPlural } = UNIT_TYPE_REGISTRY[unit];
    return count === 1 ? labelSingular : labelPlural;
}
export const ACTIVITY_TYPES = [
    'GENERAL',
    'MEETING',
    'EMAIL',
    'PHONE',
    'MESSAGE',
    'COURT_ATTENDANCE',
    'DOCUMENT_REVIEW',
    'RESEARCH',
    'DRAFTING',
    'TRAVEL_CAR',
    'PRINTING',
    'POSTAGE',
    'ADVOCATE',
    'CORRESPONDENT_ATTORNEY',
    'SHERIFF',
    'GOVERNMENT_GAZETTE',
];
// The billing increment is input-time steering only, never a read-time
// rounding rule (see time-increment.ts): `amountMinorUnits` is the stored
// financial source of truth at every read path, and no aggregation query
// consults the increment. `unitType = 'hours'` is still the switch that
// matters here, but for a different reason - it is the only unit type the
// increment steers at authoring time (warnings + `applyTimeIncrement`, both
// in time-increment.ts). If you ever add a new UnitType that should be
// steered by the practice's timeIncrement, extend those authoring-time call
// sites, not a read-time aggregate.
export const ACTIVITY_TYPE_REGISTRY = {
    GENERAL: { label: 'General', defaultUnitType: 'hours', allowedUnitTypes: UNIT_TYPES, defaultIsDisbursement: false },
    MEETING: { label: 'Meeting', defaultUnitType: 'hours', allowedUnitTypes: ['hours', 'lump_sum'], defaultIsDisbursement: false },
    EMAIL: { label: 'Email', defaultUnitType: 'hours', allowedUnitTypes: ['hours', 'items', 'lump_sum'], defaultIsDisbursement: false },
    PHONE: { label: 'Phone call', defaultUnitType: 'hours', allowedUnitTypes: ['hours', 'lump_sum'], defaultIsDisbursement: false },
    // Per-message correspondence billing - WhatsApp, SMS, Signal, Teams chat.
    // `hours` stays allowed because a long voice note plus a considered reply is
    // time rather than a count. There is deliberately no per-message rate
    // cascade (unlike EMAIL's FIXED_RATE mode): `messages` is not one of the
    // units `hasDefaultRateSource` resolves, so the wizard clears the rate field
    // and the user enters an explicit rate on each row.
    MESSAGE: { label: 'Message', defaultUnitType: 'messages', allowedUnitTypes: ['messages', 'hours', 'lump_sum'], defaultIsDisbursement: false },
    COURT_ATTENDANCE: { label: 'Court attendance', defaultUnitType: 'hours', allowedUnitTypes: ['hours', 'appearances', 'lump_sum'], defaultIsDisbursement: false, practices: ['ATTORNEY', 'ADVOCATE'] },
    DOCUMENT_REVIEW: { label: 'Document review', defaultUnitType: 'hours', allowedUnitTypes: ['hours', 'pages', 'documents', 'lump_sum'], defaultIsDisbursement: false },
    RESEARCH: { label: 'Research', defaultUnitType: 'hours', allowedUnitTypes: ['hours', 'lump_sum'], defaultIsDisbursement: false },
    DRAFTING: { label: 'Drafting', defaultUnitType: 'hours', allowedUnitTypes: ['hours', 'pages', 'documents', 'lump_sum'], defaultIsDisbursement: false },
    TRAVEL_CAR: { label: 'Travel (car)', defaultUnitType: 'km', allowedUnitTypes: ['km', 'hours', 'lump_sum'], defaultIsDisbursement: true },
    PRINTING: { label: 'Printing', defaultUnitType: 'pages', allowedUnitTypes: ['pages', 'lump_sum'], defaultIsDisbursement: true, practices: ['ATTORNEY', 'ADVOCATE'] },
    POSTAGE: { label: 'Postage', defaultUnitType: 'lump_sum', allowedUnitTypes: ['lump_sum', 'items'], defaultIsDisbursement: true, practices: ['ATTORNEY', 'ADVOCATE'] },
    ADVOCATE: { label: 'Advocate fees', defaultUnitType: 'lump_sum', allowedUnitTypes: ['lump_sum', 'hours', 'appearances'], defaultIsDisbursement: true, deprecated: true },
    CORRESPONDENT_ATTORNEY: { label: 'Correspondent attorney fees', defaultUnitType: 'lump_sum', allowedUnitTypes: ['lump_sum', 'hours'], defaultIsDisbursement: true, deprecated: true },
    SHERIFF: { label: "Sheriff's fees", defaultUnitType: 'lump_sum', allowedUnitTypes: ['lump_sum', 'items'], defaultIsDisbursement: true, deprecated: true },
    GOVERNMENT_GAZETTE: { label: 'Government Gazette', defaultUnitType: 'lump_sum', allowedUnitTypes: ['lump_sum'], defaultIsDisbursement: true, deprecated: true },
};
export const OTHER_ACTIVITY_TYPES = ACTIVITY_TYPES.filter((t) => t !== 'GENERAL');
/**
 * Activity types whose registry default is a professional fee
 * (`defaultIsDisbursement: false`). GENERAL is included. Remember that the
 * row-level `isDisbursement` column is authoritative after create; the
 * registry default only controls the wizard's pre-fill and the chat agent's
 * default when the user does not specify.
 */
export const PROFESSIONAL_FEE_TYPES = ACTIVITY_TYPES.filter((type) => !ACTIVITY_TYPE_REGISTRY[type].defaultIsDisbursement);
/**
 * Activity types whose registry default is a disbursement
 * (`defaultIsDisbursement: true`). Excludes GENERAL. See PROFESSIONAL_FEE_TYPES
 * for the caveat about the row-level override.
 */
export const DISBURSEMENT_TYPES = ACTIVITY_TYPES.filter((type) => ACTIVITY_TYPE_REGISTRY[type].defaultIsDisbursement);
/**
 * Activity types still offered for *new* capture - the activity wizard's type
 * tiles and the chat agent's create guidance both render this set, not the full
 * `ACTIVITY_TYPES`. Excludes types flagged `deprecated` (third-party costs that
 * now belong in Expenses). The deprecated types remain valid stored values for
 * existing rows; they are just no longer presented as a choice when logging new
 * work. See `ActivityTypeConfig.deprecated`.
 *
 * This is the practice-invariant baseline (equivalent to what an ATTORNEY /
 * ADVOCATE account sees). It backs the static, module-load-time MCP/Zod field
 * descriptions (`formatUnitTypeDefaultsAndOverrides`, `formatIsDisbursementDefaults`),
 * which cannot vary per request. When the caller's practice type is known at
 * call time (the wizard, the per-conversation chat glossary), use
 * `getCaptureActivityTypes(practiceType)` instead, which additionally applies
 * the per-type `practices` inclusion list.
 */
export const CAPTURE_ACTIVITY_TYPES = ACTIVITY_TYPES.filter((type) => !ACTIVITY_TYPE_REGISTRY[type].deprecated);
/**
 * Practice-aware capture list: `CAPTURE_ACTIVITY_TYPES` further filtered by
 * each type's `practices` inclusion list (today: BUSINESS loses the
 * legal-flavoured COURT_ATTENDANCE, PRINTING, and POSTAGE). A null or
 * undefined practice type (legacy rows) resolves to 'ATTORNEY' - the same
 * attorney default as `DEFAULT_PRACTICE_TYPE` in `@packages/practice-config`
 * (restated here because that package depends on this one). Use this at the
 * surfaces that know the account's practice type when the caller offers or
 * picks a new activity type - the wizard's type tiles and the chat agent's
 * per-conversation glossary. Composes with `deprecated`: a type is excluded
 * when either applies, not by a second parallel mechanism.
 *
 * The enum value stays valid everywhere else regardless of practice type -
 * this only governs what is *offered*, never what the service or API accepts.
 */
export function getCaptureActivityTypes(practiceType) {
    const resolved = practiceType ?? 'ATTORNEY';
    return CAPTURE_ACTIVITY_TYPES.filter((type) => {
        const { practices } = ACTIVITY_TYPE_REGISTRY[type];
        return practices === undefined || practices.includes(resolved);
    });
}
/**
 * Activity types retired from new capture (`deprecated: true`). These are the
 * third-party costs now captured as Expenses (supplier invoices). Used to build
 * the chat agent's "record this as an expense instead" steer.
 */
export const DEPRECATED_ACTIVITY_TYPES = ACTIVITY_TYPES.filter((type) => ACTIVITY_TYPE_REGISTRY[type].deprecated);
/**
 * True when the given type is retired from new capture. Accepts a free-form
 * string (and null) so it can be called at trust boundaries. A null or unknown
 * type is not deprecated.
 */
export function isDeprecatedActivityType(type) {
    if (type === null)
        return false;
    return ACTIVITY_TYPE_REGISTRY[type]?.deprecated === true;
}
/**
 * Returns the registry config for a given activity type, or null for a null
 * type (free-text / ad hoc entry).
 */
export function getActivityType(type) {
    if (type === null)
        return null;
    return ACTIVITY_TYPE_REGISTRY[type];
}
export function getTypeInfo(type) {
    if (type === null)
        return null;
    const info = ACTIVITY_TYPE_REGISTRY[type];
    return info ?? null;
}
export function getTypeLabel(type) {
    if (type === null)
        return 'General';
    return getTypeInfo(type)?.label ?? 'General';
}
/**
 * True when the given unitType is in the allowedUnitTypes list for the given
 * activity type. For `type === null` (legacy free-text rows) any UnitType is
 * accepted. The service layer calls this to reject wizard/chat/CSV submissions
 * that try to pair an incompatible type/unit (e.g. MEETING with unitType
 * 'pages').
 */
export function isUnitTypeAllowedForActivityType(type, unit) {
    if (type === null)
        return true;
    return ACTIVITY_TYPE_REGISTRY[type].allowedUnitTypes.includes(unit);
}
/**
 * Render the per-type unitType defaults + allowed overrides as a compact
 * string for embedding in tool / field descriptions. Derived directly from
 * `ACTIVITY_TYPE_REGISTRY` so the description cannot drift from the map.
 *
 * Example line: `MEETING: default=hours, override=lump_sum`.
 */
export function formatUnitTypeDefaultsAndOverrides() {
    return CAPTURE_ACTIVITY_TYPES.map((type) => {
        const cfg = ACTIVITY_TYPE_REGISTRY[type];
        const overrides = cfg.allowedUnitTypes.filter((u) => u !== cfg.defaultUnitType);
        const overridePart = overrides.length > 0 ? `, override=${overrides.join('|')}` : ', no overrides';
        return `- ${type}: default=${cfg.defaultUnitType}${overridePart}`;
    }).join('\n');
}
/**
 * Render the per-type isDisbursement defaults as a compact string. Derived
 * directly from `ACTIVITY_TYPE_REGISTRY.defaultIsDisbursement`. Deprecated types
 * are omitted - they are no longer offered for new capture (see
 * `CAPTURE_ACTIVITY_TYPES`).
 */
export function formatIsDisbursementDefaults() {
    const trueTypes = CAPTURE_ACTIVITY_TYPES.filter((t) => ACTIVITY_TYPE_REGISTRY[t].defaultIsDisbursement);
    const falseTypes = CAPTURE_ACTIVITY_TYPES.filter((t) => !ACTIVITY_TYPE_REGISTRY[t].defaultIsDisbursement);
    return `Defaults to true for ${trueTypes.join('/')}; false for ${falseTypes.join('/')}.`;
}
/**
 * One-line steer telling the chat agent NOT to log the deprecated third-party
 * cost types as activities, and to record them as expenses instead. Derived from
 * `DEPRECATED_ACTIVITY_TYPES` so it cannot drift from the registry. Returns an
 * empty string when nothing is deprecated.
 */
export function formatDeprecatedActivityTypeGuidance() {
    if (DEPRECATED_ACTIVITY_TYPES.length === 0)
        return '';
    const labelled = DEPRECATED_ACTIVITY_TYPES.map((type) => `${type} (${ACTIVITY_TYPE_REGISTRY[type].label})`).join(', ');
    return `Third-party costs - ${labelled} - are expenses, never time entries: record them with createSupplierInvoice.`;
}
